import {
  DataGrid,
  GridPagination as MuiDataGridPagination,
} from "@mui/x-data-grid";
import { Autocomplete } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Card,
  CardHeader,
  CardContent,
  useMediaQuery,
  Typography,
  Box,
  Grid2 as Grid,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  FormControl,
  Checkbox,
  Select,
  MenuItem,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { green, grey, red } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { LoadingButton } from "@mui/lab";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getPermitPackages,
  handleDownloadNoc,
  setPrevTourStep,
  updatePermitPackage,
} from "../../store/actions/dataActions";
import { useTour } from "@reactour/tour";
import { tourSteps } from "../../data/walkthroughDemo/steps";
import { packageColumns } from "./permitPackageTabs/permitTypeColumns";
import {
  noRecordTitle,
  packageRowDetailsEmptyTitle,
} from "../../data/mappings/permitPackageMappings";
import { isCurrentFormNOC } from "../permitForms/permitFormHelpers";
import SelectDownloadFileSIzeModal from "../modals/SelectDownloadFileSIzeModal";
import PermitPackageMiddleContent from "./middleContent/PermitPackageMiddleContent";
import CustomDocumentMiddleContent from "./middleContent/CustomDocumentMiddleContent";
import PermitPackageRightContent from "./rightContent/PermitPackageRightContent";
import CustomDocumentRightContent from "./rightContent/CustomDocumentRightContent";
import {
  setActiveTab,
  setAttachingNewPermit,
  setLeftValue,
  setMyPermitsSnackOpen,
  setNumCreated,
  setPage,
  setPageSize,
  setPermitLoadings,
  setRealEstateAddressOptions,
  setRightValue,
  setRowCount,
  setSelectedRealEstateAddressTemp,
  setSelectedRow,
  setSelectedPermitType,
} from "../../store/actions/uiActions/myPermitsActions";

// Configure dayjs with UTC plugin
dayjs.extend(utc);

const MyPermitGrid = ({
  gridKey,
  selectionModel,
  setSelectionModel,
  setDocNotaryTypeValue,
  docNotaryTypeValue,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const prevRowId = useRef(null);
  const isSelectingRow = useRef(false);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const permitPackages = useSelector((state) => state.nocData?.permitPackages);
  const currentUser = useSelector((state) => state[currentSession].user);
  const { cancelPermitLoading, ppDownloading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings,
  );

  const { myPermitsSnackOpen, myPermitsSnackText, numCreated } = useSelector(
    (state) => state.myPermitsComponent.permits,
  );

  const { rightValue, activeTab, selectedRow, leftValue, selectedPermitType } =
    useSelector((state) => state.myPermitsComponent);

  const { permitDrawerOpen } = useSelector(
    (state) => state.myPermitsComponent.permits.modalStates,
  );

  const { dataLoading, activeTabLoading, tempLoading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings,
  );
  const { page, rowCount } = useSelector(
    (state) => state.myPermitsComponent.permits.pagination,
  );

  const [fileSizeModalOpen, setFileSizeModalOpen] = useState(false);
  const [recordLoading, setRecordLoading] = useState(false);
  const [currentLeftNotarized, setCurrentLeftNotarized] = useState(false);
  const [currentLeftInProgress, setCurrentLeftInProgress] = useState(false);
  const [permitOptions, setPermitOptions] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [mobileDetailsView, setMobileDetailsView] = useState("docs");

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const autoCompleteRef = useRef(null);

  // Keep ref in sync with permitPackages
  useEffect(() => {
    permitPackagesRef.current = permitPackages;
  }, [permitPackages]);

  // Cache for pagination data to prevent unnecessary API calls
  const [paginationCache, setPaginationCache] = useState({});
  const skipNextPaginationRef = useRef(false);
  const permitPackagesRef = useRef(permitPackages);

  // Clean up old cache entries (older than 5 minutes)
  useEffect(() => {
    const cleanupCache = () => {
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      setPaginationCache((prev) => {
        const cleaned = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (now - value.timestamp < fiveMinutes) {
            cleaned[key] = value;
          }
        });
        return cleaned;
      });
    };

    const interval = setInterval(cleanupCache, 60000); // Clean every minute
    return () => clearInterval(interval);
  }, []);
  const [searchFilters, setSearchFilters] = useState({
    includeSubuserPackages: false,
    subuserEmail: "",

    // Text fields
    packageName: "",
    projectAddress: "",
    propertyAddress: "",
    propertyOwnerName: "",
    contractorLicenseNumber: "",
    contractorLicenseType: "",
    contractorName: "",
    county: "",
    city: "",

    // Yes/No fields
    nocCreated: "",
    permitPackageCreated: "",
    notarized: "",
    recorded: "",
    connectedToCity: "",

    // Date fields
    initiatedFrom: "",
    initiatedUntil: "",
  });

  // State for current applied filters
  const [currentFilters, setCurrentFilters] = useState(null);
  const currentFiltersRef = useRef(null);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  // Check if any filters are applied
  const hasActiveFilters =
    currentFilters && Object.keys(currentFilters).length > 0;

  // Update ref when currentFilters changes
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  const [autocompleteOptions, setAutocompleteOptions] = useState({
    packageNames: ["N/A"],
    projectAddresses: ["N/A"],
    propertyAddresses: ["N/A"],
    propertyOwnerNames: ["N/A"],
    contractorLicenseNumbers: ["N/A"],
    contractorLicenseTypes: ["N/A"],
    contractorNames: ["N/A"],
    counties: ["N/A"],
    cities: ["N/A"],
  });

  const isBranchManager = Boolean(currentUser?.user?.enterprise?.is_branch);

  const branchSubuserOptions = useMemo(() => {
    const source = Array.isArray(currentUser?.subusers_row)
      ? currentUser.subusers_row
      : Array.isArray(currentUser?.subusers)
        ? currentUser.subusers
        : [];

    return source
      .map((subuser) => {
        const fullName =
          subuser?.name ||
          [subuser?.first, subuser?.last].filter(Boolean).join(" ") ||
          subuser?.email ||
          "";

        return {
          label: fullName,
          email: subuser?.email || "",
        };
      })
      .filter((subuser) => subuser.email);
  }, [currentUser?.subusers, currentUser?.subusers_row]);

  const activeUserEmail = (
    currentUser?.subuser?.email ||
    currentUser?.user?.email ||
    currentUser?.email ||
    ""
  )
    ?.toLowerCase()
    ?.trim();

  const isSubuserPackageRow = useCallback(
    (row) =>
      Boolean(
        isBranchManager &&
          row?.email &&
          activeUserEmail &&
          row.email.toLowerCase().trim() !== activeUserEmail,
      ),
    [activeUserEmail, isBranchManager],
  );

  const selectedRowIsSubuserPackage = isSubuserPackageRow(selectedRow);

  const { isOpen, currentStep, setCurrentStep } = useTour();

  const MOBILE_BREAKPOINT = "(max-width:900px)";

  const lg = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const isMobilePermits = useMediaQuery(MOBILE_BREAKPOINT);
  const selectedDocsLength = selectedRow?.selected_documents?.length;

  // Fetch aggregation data for autocomplete options
  const fetchAutocompleteOptions = useCallback(async () => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/get-permit-package-aggregations`,
        {
          currentPath: window.location.pathname,
          currentSession: currentSession,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success && resp?.data?.data) {
        const data = resp.data.data;
        // Convert cities to title case
        const formattedCities = data.cities?.map((city) =>
          city === "N/A"
            ? city
            : city.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        ) || ["N/A"];

        setAutocompleteOptions({
          ...data,
          cities: formattedCities,
        });
      }
    } catch (error) {
      console.error("Error fetching autocomplete options:", error);
    }
  }, [currentSession]);

  // Fetch autocomplete options on component mount
  useEffect(() => {
    if (currentSession && !autoCompleteRef?.current) {
      autoCompleteRef.current = true;
      fetchAutocompleteOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  const handleAttachingNewPermit = useCallback(() => {
    const selectedCounty = currentUser?.user?.selectedCountiesNOC?.find(
      (county) => county.name === selectedRow?.county,
    );
    // collect used contractors from main and additional permits
    const usedContractors = [];
    if (selectedRow?.contractor) usedContractors.push(selectedRow.contractor);
    if (selectedRow?.additional_permits) {
      Object.values(selectedRow.additional_permits).forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((p) => {
            if (p?.contractor) usedContractors.push(p.contractor);
          });
        }
      });
    }
    const attachingPayload = {
      started: true,
      taskId: selectedRow?.task_id,
      county: selectedRow?.county,
      city: selectedRow?.city,
      form: selectedCounty,
      addedPermits: permitOptions,
      additionalPermits: selectedRow?.additional_permits || {},
      usedContractors: usedContractors,
      packageSnapshot: selectedRow,
      prevPermitGrid: {
        page: page,
        pageSize: rowsPerPage,
        rowCount: rowCount,
      },
    };
    dispatch(setAttachingNewPermit(attachingPayload));
    navigate(`/permits/create`, {
      state: { attachingNewPermit: attachingPayload },
    });
  }, [
    dispatch,
    selectedRow,
    permitOptions,
    navigate,
    currentUser?.user?.selectedCountiesNOC,
    page,
    rowsPerPage,
    rowCount,
  ]);

  const updateSelectedPackageTypeData = useCallback(
    async (index) => {
      try {
        await dispatch(
          setPermitLoadings({ activeTabLoading: index, dataLoading: true }),
        );
        const result = await dispatch(
          getPermitPackages({
            newPage: 0,
            newPageSize: rowsPerPage,
            filters: currentFiltersRef.current,
          }),
        );

        if (result && result.success) {
          // Ensure we pass a number to setRowCount
          const rowCount = typeof result.data === "number" ? result.data : 0;
          dispatch(setRowCount(rowCount));
          dispatch(setPage(0)); // Reset to first page on successful tab change
        } else {
          dispatch(
            setMyPermitsSnackOpen({
              isOpen: true,
              snackText: "Failed to load permit packages. Please try again.",
            }),
          );
        }
      } catch (error) {
        console.error("Error updating package type data:", error);
        dispatch(
          setMyPermitsSnackOpen({
            isOpen: true,
            snackText:
              "Network error. Please check your connection and try again.",
          }),
        );
      } finally {
        await dispatch(
          setPermitLoadings({ activeTabLoading: null, dataLoading: false }),
        );
      }
    },
    [dispatch, rowsPerPage],
  );

  const handlePackageTypeChange = useCallback(
    async (index) => {
      if (activeTab === index) return;

      dispatch(setActiveTab(index));
      dispatch(setSelectedRow(null));
      // Don't update page state immediately - let updateSelectedPackageTypeData handle it on success
      updateSelectedPackageTypeData(index);
    },
    [activeTab, updateSelectedPackageTypeData, dispatch],
  );

  const toTitleCase = (s = "") =>
    s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const formatCity = useCallback((city) => {
    if (!city) return "N/A";
    return city
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  const formatDateFromUnix = useCallback((seconds, variant = "full") => {
    if (!seconds) return "N/A";
    const date = new Date(seconds * 1000);
    if (isNaN(date.getTime())) return "N/A";
    const opts =
      variant === "short"
        ? { month: "short", day: "numeric" }
        : { month: "short", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", opts).format(date);
  }, []);

  const formatDateFromISO = useCallback((isoDate, variant = "full") => {
    if (!isoDate) return "N/A";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "N/A";
    const opts =
      variant === "short"
        ? { month: "short", day: "numeric" }
        : { month: "short", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", opts).format(date);
  }, []);

  const statusItem = useCallback((label, value, allowNA = false) => {
    const isNA = allowNA && value === null;

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.3,
        }}
      >
        <Typography sx={{ fontSize: "0.68rem", color: "rgba(0, 0, 0, 0.65)" }}>
          {label}
        </Typography>
        {isNA ? (
          <Typography
            sx={{ fontSize: "0.68rem", color: "rgba(0, 0, 0, 0.65)" }}
          >
            N/A
          </Typography>
        ) : value ? (
          <CheckCircleIcon sx={{ color: green[500], fontSize: "0.95rem" }} />
        ) : (
          <CancelIcon sx={{ color: red[500], fontSize: "0.95rem" }} />
        )}
      </Box>
    );
  }, []);

  // Get selected documents based on the selected permit type
  const getSelectedDocumentsForPermitType = useCallback(
    (permitType) => {
      if (!selectedRow || !permitType) return [];

      try {
        // If it's the main permit type, return main selected_documents
        if (permitType === selectedRow.permit_type) {
          return Array.isArray(selectedRow.selected_documents)
            ? selectedRow.selected_documents
            : [];
        }

        // If it's an additional permit, find it in additional_permits
        if (
          selectedRow.additional_permits &&
          selectedRow.additional_permits[permitType]
        ) {
          const additionalPermit = selectedRow.additional_permits[permitType];
          if (Array.isArray(additionalPermit)) {
            // If it's an array, get the first one (most recent)
            return Array.isArray(additionalPermit[0]?.selected_documents)
              ? additionalPermit[0].selected_documents
              : [];
          } else {
            // If it's a single object
            return Array.isArray(additionalPermit.selected_documents)
              ? additionalPermit.selected_documents
              : [];
          }
        }

        return [];
      } catch (error) {
        return [];
      }
    },
    [selectedRow],
  );

  const compactStatusChip = (label, value, allowNA = false) => {
    const isNA = allowNA && value === null;

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
          {label}
        </Typography>
        {isNA ? (
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
            N/A
          </Typography>
        ) : value ? (
          <CheckCircleIcon sx={{ color: green[500], fontSize: "1rem" }} />
        ) : (
          <CancelIcon sx={{ color: red[500], fontSize: "1rem" }} />
        )}
      </Box>
    );
  };

  const mobileGridColumnsByTab = useMemo(() => {
    // One column. Everything stacked inside renderCell.
    const col = {
      field: "mobile",
      headerName: "Packages",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const row = params?.row || {};
        const isCustomDoc = Boolean(row?.cst_packageID);

        const packageName = isCustomDoc
          ? "Custom Documents"
          : row?.package_name || "N/A";

        const projectAddress = isCustomDoc
          ? row?.document_location?.project_address || "N/A"
          : row?.project_address || "N/A";

        const county = isCustomDoc
          ? row?.document_location?.county || "N/A"
          : row?.county || "N/A";

        const city = isCustomDoc ? "N/A" : formatCity(row?.city);

        // Dates
        let initiatedDate = "N/A";
        if (isCustomDoc) {
          const canNotarize = row?.document?.actions?.notarize;
          const canRecord = row?.document?.actions?.record;
          const startedAt =
            (canNotarize && canRecord) || canNotarize
              ? row?.processing_permit?.notarization?.started_at
              : canRecord
                ? row?.processing_permit?.recording?.started_at
                : null;
          initiatedDate = formatDateFromISO(startedAt, "short");
        } else {
          initiatedDate = formatDateFromUnix(
            row?.permit_package_added_at,
            "short",
          );
        }

        // Status flags (match your existing logic)
        const nocCreated = isCustomDoc ? null : Boolean(row?.noc_created);

        const hasMultipleDocs = (row?.selected_documents?.length || 0) > 1;
        const ppCreated = isCustomDoc
          ? null
          : Boolean(row?.permit_package_created && hasMultipleDocs);

        const notarized = isCustomDoc
          ? row?.document?.actions?.notarize
            ? Boolean(row?.notarized)
            : null
          : Boolean(row?.notarized);

        const recorded = isCustomDoc
          ? row?.document?.actions?.record
            ? Boolean(
                row?.processing_permit?.recording?.recorded || row?.recorded,
              )
            : null
          : Boolean(row?.recorded);

        // Tab-specific: "Sent to Contractors"
        const assignedTo = row?.assigned_package?.user_email || "N/A";
        const sentDate = row?.assigned_package?.assigned_at
          ? formatDateFromUnix(row?.assigned_package?.assigned_at, "short")
          : "N/A";

        return (
          <Box
            sx={{
              width: "100%",
              py: 0.6,
              px: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.45,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {packageName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "rgba(0,0,0,0.62)",
                    lineHeight: 1.2,
                    mt: 0.25,
                    wordBreak: "break-word",
                  }}
                >
                  {projectAddress}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    color: "rgba(0,0,0,0.62)",
                    lineHeight: 1.2,
                    mt: 0.15,
                  }}
                >
                  {county} • {city}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "rgba(0,0,0,0.62)",
                  flexShrink: 0,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {activeTab === 1 ? sentDate : initiatedDate}
              </Typography>
            </Box>

            {activeTab === 1 ? (
              <Typography sx={{ fontSize: "0.82rem" }}>{assignedTo}</Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.0 }}>
                {compactStatusChip("NOC Created", nocCreated, true)}
                {compactStatusChip("Package Built", ppCreated, true)}
                {compactStatusChip("Notarized", notarized, true)}
                {compactStatusChip("Recorded", recorded, true)}
              </Box>
            )}
          </Box>
        );
      },
    };

    return { 0: [col], 1: [col], 2: [col] };
  }, [activeTab, formatCity, formatDateFromISO, formatDateFromUnix]);

  useEffect(() => {
    let permitOptions = [];

    if (selectedRow?.permit_type) {
      permitOptions.push(selectedRow.permit_type);
    }

    if (
      selectedRow?.additional_permits &&
      Object.keys(selectedRow.additional_permits).length > 0
    ) {
      permitOptions.push(...Object.keys(selectedRow.additional_permits));
    }

    // Remove duplicates and filter out any invalid values
    permitOptions = [...new Set(permitOptions)].filter(
      (option) => option && typeof option === "string" && option.trim() !== "",
    );

    setPermitOptions(permitOptions);

    // Always ensure selectedPermitType is valid for the current row
    if (permitOptions.length > 0) {
      // Check if current selectedPermitType is valid for this row
      const isValidPermitType = permitOptions.includes(selectedPermitType);

      if (isSelectingRow.current || !isValidPermitType) {
        // Set to first available option if invalid or during row selection
        dispatch(setSelectedPermitType(permitOptions[0]));
        isSelectingRow.current = false;
      }
    } else {
      // Clear selectedPermitType if no options available
      if (selectedPermitType) {
        dispatch(setSelectedPermitType(""));
      }
    }
  }, [selectedRow, selectedPermitType, dispatch]);

  useEffect(() => {
    if (selectedRow) {
      const createdCount = selectedRow?.selected_documents?.filter(
        (doc) => doc.created === true,
      ).length;

      if (numCreated !== createdCount) {
        dispatch(setNumCreated(createdCount));
      }
    }
  }, [selectedRow, numCreated, dispatch]);

  useEffect(() => {
    const isNOC = isCurrentFormNOC(selectedRow, selectedPermitType, leftValue);

    // Get the current document name and its contractor owner
    const currentDocName = selectedRow?.selected_documents?.[leftValue]?.name;
    const currentDocData = currentDocName
      ? selectedRow?.documents?.[currentDocName]
      : null;
    const docContractorLicense = currentDocData?.contractor;

    // Check if this document belongs to an additional contractor
    const additionalContractors =
      selectedRow?.processing_permit?.notarization?.additional_contractors ||
      [];
    const additionalContractorForDoc = additionalContractors.find(
      (ac) => ac?.license_number === docContractorLicense,
    );

    // Determine if the document is notarized based on who owns it
    let notarized = false;

    if (additionalContractorForDoc) {
      // Document belongs to an additional contractor - check their status
      notarized =
        additionalContractorForDoc?.notarization?.is_notarized === true ||
        additionalContractorForDoc?.notarization?.is_signed === true;
    } else {
      // Document belongs to main contractor (or no specific contractor) - use standard logic
      notarized =
        selectedRow?.notarized ||
        (isNOC && selectedRow?.noc_notarized === true) ||
        (selectedRow?.selected_documents?.[leftValue]?.name ===
          "noc_affidavit" &&
          selectedRow?.noc_notarized === true &&
          selectedRow?.documents?.noc_affidavit?.is_created === true) ||
        (leftValue >= 1 && selectedRow?.pp_notarized === true)
          ? true
          : false;
    }

    if (notarized !== currentLeftNotarized) {
      setCurrentLeftNotarized(notarized);
    }

    const notarizingDocs =
      selectedRow?.processing_permit?.notarization?.notarizing_docs;
    const finalized = selectedRow?.processing_permit?.notarization?.finalized;
    const started = selectedRow?.processing_permit?.notarization?.started;

    const inProgress =
      notarizingDocs === 0 && started === true && !finalized
        ? true
        : (notarizingDocs === 1 && isNOC && started === true && !finalized) ||
            (notarizingDocs === 1 &&
              selectedRow?.selected_documents?.[leftValue]?.name ===
                "noc_affidavit" &&
              started === true &&
              !finalized)
          ? true
          : notarizingDocs === 2 && !isNOC && started === true && !finalized
            ? true
            : false;

    const currentProgress = !notarized && inProgress ? true : false;

    if (currentLeftInProgress !== currentProgress) {
      setCurrentLeftInProgress(currentProgress);
    }
  }, [
    leftValue,
    selectedRow,
    currentLeftInProgress,
    currentLeftNotarized,
    selectedPermitType,
  ]);

  const snackHandleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(setMyPermitsSnackOpen({ isOpen: false, snackText: "" }));
  };

  const handleLeftChange = useCallback(
    (event, newValue) => {
      if (leftValue !== newValue) {
        if (selectedDocsLength === 1) {
          dispatch(setLeftValue(0));
        } else {
          dispatch(setLeftValue(newValue));
        }
      }
    },
    [leftValue, selectedDocsLength, dispatch],
  );

  useEffect(() => {
    if (isOpen) {
      const stepForRight = currentSession === "userSession" ? 15 : 13;

      if (currentStep === stepForRight && rightValue !== 0) {
        dispatch(setRightValue(0));
      }
    }
  }, [rightValue, currentStep, currentSession, isOpen, dispatch]);

  const fetchData = useCallback(
    async (
      newPage,
      newRowsPerPage = null,
      overrideFilters,
      forceRefresh = false,
    ) => {
      try {
        await dispatch(setPermitLoadings({ dataLoading: true }));
        const pageSizeToUse =
          newRowsPerPage !== null ? newRowsPerPage : rowsPerPage;
        const effectiveFilters =
          overrideFilters !== undefined
            ? overrideFilters
            : currentFiltersRef.current;

        // Create cache key
        const cacheKey = `${activeTab}_${newPage}_${pageSizeToUse}_${JSON.stringify(effectiveFilters || {})}`;

        // Check cache first
        if (!forceRefresh && paginationCache[cacheKey]) {
          const cachedData = paginationCache[cacheKey];
          dispatch(setPage(newPage));
          dispatch(setPageSize(pageSizeToUse));
          dispatch(setRowCount(cachedData.rowCount));
          // Use fresh documents from cache
          await dispatch({
            type: "GET_PERMIT_PACKAGES",
            payload: [...(cachedData.documents || [])],
          });
          await dispatch(setPermitLoadings({ dataLoading: false }));
          return;
        }

        const result = await dispatch(
          getPermitPackages({
            newPage: newPage,
            newPageSize: pageSizeToUse,
            filters: effectiveFilters,
          }),
        );

        // Only update pagination state if the request was successful
        if (result && result.success) {
          dispatch(setPage(newPage));
          dispatch(setPageSize(pageSizeToUse));
          // Update rowCount with the total count from the API response
          if (typeof result.data === "number") {
            dispatch(setRowCount(result.data));

            // Cache the result with fresh documents from ref (updated after dispatch)
            // Use a small delay to ensure Redux state is updated
            setTimeout(() => {
              const freshDocuments = permitPackagesRef.current || [];

              // Cache the result with fresh documents
              setPaginationCache((prev) => ({
                ...prev,
                [cacheKey]: {
                  documents: [...freshDocuments], // Create a copy to avoid reference issues
                  rowCount: result.data,
                  timestamp: Date.now(),
                },
              }));
            }, 0);
          }
        } else {
          // If request failed, show error notification
          dispatch(
            setMyPermitsSnackOpen({
              isOpen: true,
              snackText: "Failed to load permit packages. Please try again.",
            }),
          );
        }
      } catch (error) {
        console.error("Error fetching permit packages:", error);
        dispatch(
          setMyPermitsSnackOpen({
            isOpen: true,
            snackText:
              "Network error. Please check your connection and try again.",
          }),
        );
      } finally {
        await dispatch(setPermitLoadings({ dataLoading: false }));
      }
    },
    [dispatch, rowsPerPage, activeTab, paginationCache],
  );

  const handlePaginationModelChange = useCallback(
    (model, details) => {
      if (skipNextPaginationRef.current) {
        skipNextPaginationRef.current = false;
        return;
      }
      const newPage = model.page;
      const newRowsPerPage = model.pageSize;

      // Clear selected row immediately for better UX
      dispatch(setSelectedRow(null));

      // Update local rowsPerPage state if it changed
      if (newRowsPerPage !== rowsPerPage) {
        setRowsPerPage(newRowsPerPage);
      }

      // Fetch data - pagination state will be updated only on success
      fetchData(newPage, newRowsPerPage);
    },
    [fetchData, dispatch, rowsPerPage],
  );

  const handleStartRecord = useCallback(async () => {
    try {
      setRecordLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/start-manual-recording`,
        {
          task_id: selectedRow?.task_id,
          currentSession,
        },
        {
          withCredentials: true,
        },
      );
      if (resp?.data?.success) {
        await dispatch(updatePermitPackage(resp?.data?.data));
      }
    } catch (error) {
      // optional: add error handling here
    } finally {
      setRecordLoading(false);
    }
  }, [selectedRow?.task_id, currentSession, dispatch]);

  const handleRowClick = useCallback(
    (params, event, details) => {
      if (cancelPermitLoading) {
        return;
      }
      let row = params.row;

      if (row?.cst_packageID) {
        if (selectedRow?.id !== row?.id && !isOpen) {
          dispatch(setSelectedRow(params.row));
          // Reset leftValue to 0 for custom documents
          dispatch(setLeftValue(0));
        }
      } else {
        // Only update selectedRow if it's different from the current one
        if (selectedRow?.id === row?.id) {
          return;
        }

        // Clear real estate address state if new row hasn't confirmed an address yet
        // This prevents addresses from one row appearing on another row
        let sanitizedRow = row;
        if (!row?.realEstatePresets?.realEstateSet) {
          dispatch(setRealEstateAddressOptions([]));
          dispatch(setSelectedRealEstateAddressTemp(null));
          if (row?.realEstatePresets) {
            sanitizedRow = {
              ...row,
              realEstatePresets: {
                ...row.realEstatePresets,
                realEstateSet: false,
              },
            };
          }
        }
        row = sanitizedRow;

        // Set selected row immediately for instant visual feedback
        if (selectedRow?.id !== row?.id && !isOpen) {
          isSelectingRow.current = true;
          dispatch(setSelectedRow(sanitizedRow));
        }

        if (isOpen) {
          dispatch(setPrevTourStep(currentStep));
          setTimeout(() => {
            setCurrentStep((s) => (s === tourSteps?.length - 1 ? 0 : s + 1));
          }, 200);
        }

        dispatch(setPermitLoadings({ tempLoading: false }));

        // Calculate the appropriate left value for the new row
        const newRowDocs = row?.selected_documents || [];
        let newLeftValue = 0;

        // Find the first non-created document
        for (let i = 0; i < newRowDocs.length; i++) {
          if (!newRowDocs[i]?.created) {
            newLeftValue = i;
            break;
          }
          newLeftValue = i + 1;
        }

        // Ensure we don't exceed array bounds
        if (newLeftValue >= newRowDocs.length) {
          newLeftValue = Math.max(0, newRowDocs.length - 1);
        }

        // Set the left value to the first non-created document
        if (newRowDocs.length > 0 && newLeftValue !== leftValue) {
          handleLeftChange(null, newLeftValue);
        }

        if (isOpen) {
          dispatch(setRightValue(0));
        } else if (activeTab !== 1 && !row?.assigned_package?.is_assigned) {
          const newRightValue =
            (row?.permit_package_created ||
              (row?.noc_created && row?.selected_documents?.length === 1)) &&
            !row?.notarized
              ? 2
              : row?.permit_package_created && row?.notarized && !row?.recorded
                ? 3
                : row?.notarized &&
                    !row?.recorded &&
                    (!row?.processing_permit?.recording?.is_recorded ||
                      row?.processing_permit?.recording?.recording_failed)
                  ? 3
                  : 0;

          if (newRightValue !== rightValue && !row?.cst_packageID) {
            dispatch(setRightValue(newRightValue));
          }
        } else {
          if (rightValue !== 0 && !row?.cst_packageID) {
            dispatch(setRightValue(0));
          }
        }
      }
    },
    [
      isOpen,
      currentStep,
      dispatch,
      setCurrentStep,
      activeTab,
      handleLeftChange,
      leftValue,
      rightValue,
      selectedRow?.id,
      cancelPermitLoading,
    ],
  );

  useEffect(() => {
    if (selectedRow) {
      // Set tempLoading to false if necessary
      if (tempLoading !== false) {
        dispatch(setPermitLoadings({ tempLoading: false }));
      }

      // Update selectedRow only if necessary
      let updatedRow = {};

      if (selectedRow?.cst_packageID) {
        updatedRow = permitPackages.find(
          (item) => item.cst_packageID === selectedRow.cst_packageID,
        );
      } else {
        updatedRow = permitPackages.find(
          (item) => item.task_id === selectedRow.task_id,
        );
      }

      if (updatedRow && updatedRow !== selectedRow) {
        dispatch(setSelectedRow(updatedRow));
      }
    }
  }, [permitPackages, tempLoading, dispatch, selectedRow]);

  const refreshValues = useCallback(
    (permitType) => {
      // Get the selected documents for the current permit type
      const selectedDocs = getSelectedDocumentsForPermitType(permitType);

      // If permit_package_created is true, ensure leftValue is 0
      if (selectedRow?.permit_package_created) {
        if (leftValue !== 0) {
          dispatch(setLeftValue(0));
        }
      } else {
        let firstNonCreatedIndex = 0;

        // Find the first non-created document
        for (let i = 0; i < selectedDocs?.length; i++) {
          const doc = selectedDocs?.[i];
          if (!doc?.created) {
            firstNonCreatedIndex = i;
            break;
          }
          // If all documents up to this point are created, set to next index
          firstNonCreatedIndex = i + 1;
        }

        // Ensure we don't exceed the array bounds
        if (firstNonCreatedIndex >= selectedDocs?.length) {
          firstNonCreatedIndex = Math.max(0, selectedDocs?.length - 1);
        }

        // Only set leftValue if there's more than one document and it's different
        if (selectedDocs?.length > 1 && leftValue !== firstNonCreatedIndex) {
          handleLeftChange(null, firstNonCreatedIndex);
        } else if (selectedDocs?.length === 1) {
          dispatch(setLeftValue(0));
        }
      }
    },
    [
      dispatch,
      getSelectedDocumentsForPermitType,
      handleLeftChange,
      leftValue,
      selectedRow?.permit_package_created,
    ],
  );

  // Handle permit type change
  const handlePermitTypeChange = useCallback(
    (newPermitType) => {
      // Only proceed if the new permit type is valid
      if (permitOptions.includes(newPermitType)) {
        refreshValues(newPermitType);
        dispatch(setSelectedPermitType(newPermitType));
      }
    },
    [dispatch, refreshValues, permitOptions],
  );

  useEffect(() => {
    if (!selectedRow || permitDrawerOpen) return;

    if (prevRowId.current === selectedRow.id) return; // ⛔ skip if same row

    prevRowId.current = selectedRow.id; // ✅ mark this row as seen

    if (selectedRow && !permitDrawerOpen) {
      refreshValues(selectedPermitType);
    }
  }, [permitDrawerOpen, refreshValues, selectedPermitType, selectedRow]);

  // Auto-select newly created additional permit
  useEffect(() => {
    if (selectedRow?.additional_permits && permitOptions.length > 1) {
      // Check if there's a new additional permit that wasn't in the previous options
      const newPermitType = permitOptions.find(
        (permit) => permit !== selectedRow.permit_type && !selectedPermitType,
      );

      if (newPermitType) {
        dispatch(setSelectedPermitType(newPermitType));
      }
    }
  }, [
    selectedRow?.additional_permits,
    permitOptions,
    selectedPermitType,
    selectedRow?.permit_type,
    dispatch,
  ]);

  const memoizedRightContent = useMemo(() => {
    if (!selectedRow) return null;

    return selectedRow?.cst_packageID ? (
      <CustomDocumentRightContent
        recordLoading={recordLoading}
        handleStartRecord={handleStartRecord}
        isMobile={isMobilePermits}
      />
    ) : (
      <PermitPackageRightContent
        recordLoading={recordLoading}
        handleStartRecord={handleStartRecord}
        setDocNotaryTypeValue={setDocNotaryTypeValue}
        docNotaryTypeValue={docNotaryTypeValue}
        selectedPermitType={selectedPermitType}
        isMobile={isMobilePermits}
      />
    );
  }, [
    docNotaryTypeValue,
    handleStartRecord,
    recordLoading,
    selectedRow,
    setDocNotaryTypeValue,
    selectedPermitType,
    isMobilePermits,
  ]);

  const middleContent = useMemo(() => {
    if (selectedRow?.cst_packageID) {
      return (
        <CustomDocumentMiddleContent
          setFileSizeModalOpen={setFileSizeModalOpen}
          isMobile={isMobilePermits}
        />
      );
    } else {
      return (
        <PermitPackageMiddleContent
          currentLeftNotarized={currentLeftNotarized}
          currentLeftInProgress={currentLeftInProgress}
          setFileSizeModalOpen={setFileSizeModalOpen}
          selectedPermitType={selectedPermitType}
          isMobile={isMobilePermits}
        />
      );
    }
  }, [
    setFileSizeModalOpen,
    currentLeftNotarized,
    currentLeftInProgress,
    selectedRow?.cst_packageID,
    selectedPermitType,
    isMobilePermits,
  ]);

  const mobileDocTabs = useMemo(() => {
    if (!selectedRow || selectedRow?.cst_packageID) return null;

    return (
      <Tabs
        value={leftValue}
        onChange={handleLeftChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="document tabs"
        slotProps={{
          indicator: {
            sx: {
              bottom: 0,
              height: "3px",
            },
          },
        }}
        sx={{
          width: "100%",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          px: 1,
          "& .MuiTab-root": {
            textTransform: "none",
            alignItems: "flex-start",
            minHeight: 40,
            px: 1.5,
          },
        }}
      >
        {getSelectedDocumentsForPermitType(selectedPermitType)?.map(
          (document, index) => {
            const selectedDocs =
              getSelectedDocumentsForPermitType(selectedPermitType);
            const isEnabled =
              selectedRow?.permit_package_created ||
              document?.created ||
              (selectedDocs?.[index - 1]?.created && !document?.created);

            return (
              <Tab
                disabled={!isEnabled}
                key={document?.name || index}
                label={document?.label}
                disableRipple
              />
            );
          },
        )}
      </Tabs>
    );
  }, [
    selectedRow,
    leftValue,
    selectedPermitType,
    handleLeftChange,
    getSelectedDocumentsForPermitType,
  ]);

  useEffect(() => {
    if (isMobilePermits) {
      setMobileDetailsView(selectedRow?.cst_packageID ? "status" : "docs");
    }
  }, [isMobilePermits, selectedRow?.id, selectedRow?.cst_packageID]);

  // Ensure DataGrid always receives a defined columns array
  const dataGridColumns = useMemo(() => {
    // Fallback to tab 0 columns, then to empty array as a last resort
    return packageColumns?.[activeTab] || packageColumns?.[0] || [];
  }, [activeTab]);

  const permitColumnsSmallByTab = useMemo(() => {
    const packageColumn = {
      field: "package_name",
      headerName: "Packages",
      flex: 1.3,
      sortable: false,
      renderCell: (params) => {
        const isCustomDoc = Boolean(params?.row?.cst_packageID);
        const packageName = isCustomDoc
          ? "Custom Documents"
          : params?.row?.package_name || "N/A";
        const projectAddress = isCustomDoc
          ? params?.row?.document_location?.project_address || "N/A"
          : params?.row?.project_address || "N/A";
        const county = isCustomDoc
          ? params?.row?.document_location?.county || "N/A"
          : params?.row?.county || "N/A";
        const city = isCustomDoc ? "N/A" : formatCity(params?.row?.city);

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              py: 0.7,
              pl: 1,
              pr: 0.5,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.83rem",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {packageName}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "rgba(0, 0, 0, 0.62)",
                lineHeight: 1.2,
                mt: 0.2,
              }}
            >
              {projectAddress}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "rgba(0, 0, 0, 0.62)",
                lineHeight: 1.2,
                mt: 0.2,
              }}
            >
              {county} • {city}
            </Typography>
          </Box>
        );
      },
    };

    return {
      0: [
        packageColumn,
        {
          field: "status",
          headerName: "",
          renderHeader: () => null,
          headerClassName: "tablet-empty-header",
          flex: 1,
          sortable: false,
          renderCell: (params) => {
            const isCustomDoc = Boolean(params?.row?.cst_packageID);

            let initiatedDate = "N/A";
            if (isCustomDoc) {
              const canNotarize = params?.row?.document?.actions?.notarize;
              const canRecord = params?.row?.document?.actions?.record;
              const startedAt =
                (canNotarize && canRecord) || canNotarize
                  ? params?.row?.processing_permit?.notarization?.started_at
                  : canRecord
                    ? params?.row?.processing_permit?.recording?.started_at
                    : null;
              initiatedDate = formatDateFromISO(startedAt);
            } else {
              initiatedDate = formatDateFromUnix(
                params?.row?.permit_package_added_at,
              );
            }

            const nocCreated = isCustomDoc
              ? null
              : Boolean(params?.row?.noc_created);
            const hasMultipleDocs =
              (params?.row?.selected_documents?.length || 0) > 1;
            const ppCreated = isCustomDoc
              ? null
              : Boolean(params?.row?.permit_package_created && hasMultipleDocs);

            const notarized = isCustomDoc
              ? params?.row?.document?.actions?.notarize
                ? Boolean(params?.row?.notarized)
                : null
              : Boolean(params?.row?.notarized);

            const recorded = isCustomDoc
              ? params?.row?.document?.actions?.record
                ? Boolean(
                    params?.row?.processing_permit?.recording?.recorded ||
                      params?.row?.recorded,
                  )
                : null
              : Boolean(params?.row?.recorded);

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  textAlign: "right",
                  py: 0.7,
                  pr: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0, 0, 0, 0.62)",
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  {initiatedDate}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.6,
                    justifyContent: "flex-end",
                  }}
                >
                  {statusItem("NOC", nocCreated, true)}
                  {statusItem("PP", ppCreated, true)}
                  {statusItem("Not", notarized, true)}
                  {statusItem("Rec", recorded, true)}
                </Box>
              </Box>
            );
          },
        },
      ],
      1: [
        packageColumn,
        {
          field: "assigned_to",
          headerName: "",
          renderHeader: () => null,
          headerClassName: "tablet-empty-header",
          flex: 1,
          sortable: false,
          renderCell: (params) => {
            const assignedTo =
              params?.row?.assigned_package?.user_email || "N/A";
            const sentDate = params?.row?.assigned_package?.assigned_at
              ? formatDateFromUnix(params?.row?.assigned_package?.assigned_at)
              : "N/A";

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  textAlign: "right",
                  py: 0.7,
                  pr: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0, 0, 0, 0.62)",
                    lineHeight: 1.2,
                    mb: 0.2,
                  }}
                >
                  {sentDate}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                  {assignedTo}
                </Typography>
              </Box>
            );
          },
        },
      ],
      2: [
        packageColumn,
        {
          field: "status",
          headerName: "",
          renderHeader: () => null,
          headerClassName: "tablet-empty-header",
          flex: 1,
          sortable: false,
          renderCell: (params) => {
            const initiatedDate = formatDateFromUnix(
              params?.row?.permit_package_added_at,
            );

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  textAlign: "right",
                  py: 0.7,
                  pr: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0, 0, 0, 0.62)",
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  {initiatedDate}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.6,
                    justifyContent: "flex-end",
                  }}
                >
                  {statusItem("NOC", Boolean(params?.row?.noc_created))}
                  {statusItem("Rec", Boolean(params?.row?.recorded))}
                </Box>
              </Box>
            );
          },
        },
      ],
    };
  }, [formatCity, formatDateFromISO, formatDateFromUnix, statusItem]);

  return (
    <>
      <Card
        sx={{
          position: "relative",
          mb: 0,
          pb: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          ...(isMobilePermits ? { height: "100dvh" } : null),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: isMobilePermits ? "wrap" : "nowrap",
            gap: isMobilePermits ? 1 : 0,
            px: isMobilePermits ? 1 : 0,
          }}
        >
          <CardHeader
            title={`${" \u00A0\u00A0"}P E R M I T${" \u00A0\u00A0"}P A C K A G E S`}
            sx={{
              flexGrow: 1,
              pl: 1,
              pr: 1,
              width: isMobilePermits ? "100%" : "auto",
              textAlign: isMobilePermits ? "center" : "left",
            }}
          />
          <Box
            mr={1}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: isMobilePermits ? "100%" : "auto",
              justifyContent: isMobilePermits ? "center" : "flex-start",
            }}
          >
            <Tooltip title="Search & Filter Packages">
              <Button
                variant={hasActiveFilters ? "contained" : "outlined"}
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setSearchModalOpen(true)}
                sx={{
                  textTransform: "none",
                  borderColor: hasActiveFilters
                    ? "primary.main"
                    : "rgba(0,0,0,0.23)",
                  color: hasActiveFilters ? "white" : "text.primary",
                  backgroundColor: hasActiveFilters
                    ? "primary.main"
                    : "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: hasActiveFilters
                      ? "primary.dark"
                      : "rgba(25, 118, 210, 0.04)",
                  },
                }}
              >
                Filter{" "}
                {hasActiveFilters && (
                  <Chip
                    size="small"
                    label="Active"
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: "white",
                      color: "primary.main",
                    }}
                  />
                )}
              </Button>
            </Tooltip>
          </Box>
          <Box
            mr={1}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              width: isMobilePermits ? "100%" : "auto",
              justifyContent: isMobilePermits ? "center" : "flex-start",
            }}
          >
            {isMobilePermits ? (
              <Tabs
                value={activeTab}
                onChange={(_, idx) => handlePackageTypeChange(idx)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  width: "100%",
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 1.25,
                  },
                }}
                slotProps={{
                  indicator: { sx: { height: "3px" } },
                }}
              >
                <Tab
                  label="Active"
                  disabled={Boolean(activeTabLoading) && activeTabLoading !== 0}
                />
                {!currentUser?.tier?.monthly_restart &&
                  currentSession !== "privateProviderSession" && (
                    <Tab
                      label="Sent"
                      disabled={
                        Boolean(activeTabLoading) && activeTabLoading !== 1
                      }
                    />
                  )}
                <Tab
                  label="Archived"
                  disabled={Boolean(activeTabLoading) && activeTabLoading !== 2}
                />
              </Tabs>
            ) : (
              <>
                <LoadingButton
                  disabled={
                    (Boolean(activeTabLoading) && activeTabLoading === 1) ||
                    activeTabLoading === 2
                  }
                  loading={activeTabLoading === 0}
                  variant={activeTab === 0 ? "contained" : "outlined"}
                  size="small"
                  sx={{ mr: 0.5 }}
                  onClick={() => handlePackageTypeChange(0)}
                >
                  Active Packages
                </LoadingButton>

                {!currentUser?.tier?.monthly_restart &&
                  currentSession !== "privateProviderSession" && (
                    <LoadingButton
                      disabled={
                        (Boolean(activeTabLoading) && activeTabLoading === 0) ||
                        activeTabLoading === 2
                      }
                      loading={activeTabLoading === 1}
                      size="small"
                      variant={activeTab === 1 ? "contained" : "outlined"}
                      sx={{ mr: 0.5 }}
                      onClick={() => handlePackageTypeChange(1)}
                    >
                      Packages Sent to Contractors
                    </LoadingButton>
                  )}

                <LoadingButton
                  disabled={
                    (Boolean(activeTabLoading) && activeTabLoading === 0) ||
                    activeTabLoading === 1
                  }
                  loading={activeTabLoading === 2}
                  size="small"
                  variant={activeTab === 2 ? "contained" : "outlined"}
                  onClick={() => handlePackageTypeChange(2)}
                >
                  Archived Packages
                </LoadingButton>
              </>
            )}
          </Box>
        </Box>

        <CardContent
          className="bottomCard-6"
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              className="step13"
              sx={{
                flex: isMobilePermits ? "0 0 52%" : 0.7,
                minHeight: 0,
                overflow: "hidden", // DataGrid handles scroll
                borderBottom: "5px solid #d3d3d3",
              }}
            >
              {permitPackages?.length === 0 ? (
                <Typography mt={2} align="center" color="primary" variant="h6">
                  {noRecordTitle?.[activeTab]}
                </Typography>
              ) : (
                <DataGrid
                  key={gridKey}
                  selectionModel={selectionModel}
                  onSelectionModelChange={(newSelectionModel) => {
                    setSelectionModel(newSelectionModel);
                  }}
                  slots={{
                    pagination: (props) => (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Rows per page:
                          </Typography>
                          <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                              const newRowsPerPage = e.target.value;
                              skipNextPaginationRef.current = true;
                              setRowsPerPage(newRowsPerPage);
                              // Clear cache when changing page size to ensure fresh data
                              setPaginationCache({});
                              // Reset to first page when changing rows per page
                              dispatch(setPage(0));
                              // Fetch data with new page size
                              fetchData(0, newRowsPerPage);
                            }}
                            size="small"
                            sx={{
                              minWidth: 60,
                              "& .MuiSelect-select": {
                                padding: "4px 8px",
                                fontSize: "0.75rem",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0,0,0,0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0,0,0,0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "primary.main",
                                },
                            }}
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </Box>
                        <MuiDataGridPagination {...props} />
                      </Box>
                    ),
                  }}
                  sx={{
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-main": { flex: 1, minHeight: 0 },
                    "& .MuiDataGrid-virtualScroller": {
                      flex: 1,
                      minHeight: 0,
                    },
                    "& .MuiDataGrid-virtualScrollerContent": {
                      minHeight: "100%",
                    },
                    "& .wrapHeader .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      textAlign: "center",
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-columnHeader:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      "&:hover": {
                        ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                          {
                            visibility: "hidden",
                            pointerEvents: "none",
                          },
                        cursor: "default",
                      },

                      paddingLeft: 1,
                      paddingRight: 1,
                      margin: 0,
                      backgroundColor: "#333",
                      color: "#fff",
                      borderBottom: "1px solid #ccc",
                    },
                    // Additionally target specific sort button
                    "& .MuiDataGrid-columnHeader--sortable": {
                      pointerEvents: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      padding: 0, // Adjust as needed
                      margin: 0, // Adjust as needed
                    },
                    "& .tablet-empty-header .MuiDataGrid-columnHeaderTitleContainer":
                      {
                        display: "none",
                      },
                    "& .MuiDataGrid-columnHeadersInner": {
                      width: "100%", // Adjust as needed
                      padding: 0,
                    },
                    border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid

                    "& .MuiDataGrid-cell": {
                      borderColor: tabletSize
                        ? "rgba(0, 0, 0, 0.1)"
                        : "#333333",
                      p: tabletSize ? 0 : undefined,
                    },
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none", // Optional: Hide the column separators
                    },
                    ".MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },

                    "& .MuiDataGrid-row": {
                      cursor: "pointer", // Change cursor to pointer for rows
                      borderBottom: "1px solid rgba(0,0,0,0.08)",
                    },
                    // Apply light yellow background for specified rows
                    "& .light-yellow-row": {
                      backgroundColor: "#fff4c0",
                    },
                    "& .selected-row-color": {
                      backgroundColor: "#ebf1f4 !important",
                    },
                    // Apply darker yellow only for selected rows that are light-yellow
                    "& .light-yellow-row.Mui-selected": {
                      backgroundColor: "#ffe499 !important", // Slightly darker yellow
                    },
                    ...(isMobilePermits
                      ? {
                          "& .MuiDataGrid-columnHeaders": {
                            display: "none",
                          },
                          "& .MuiDataGrid-virtualScroller": {
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            touchAction: "pan-y",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            flex: "0 0 auto",
                          },
                          "& .MuiDataGrid-cell": {
                            py: 0,
                            px: 0,
                          },
                        }
                      : {}),
                  }}
                  rows={permitPackages || []}
                  columns={
                    isMobilePermits
                      ? mobileGridColumnsByTab?.[activeTab] ||
                        mobileGridColumnsByTab?.[0]
                      : tabletSize
                        ? permitColumnsSmallByTab?.[activeTab] ||
                          permitColumnsSmallByTab?.[0]
                        : dataGridColumns
                  }
                  getRowId={(row) => row?.id}
                  onPaginationModelChange={(model, details) => {
                    handlePaginationModelChange(model, details);
                  }}
                  loading={dataLoading}
                  paginationMode="server"
                  paginationModel={{
                    page: page, // Set the current page value here
                    pageSize: rowsPerPage, // Use dynamic rows per page
                  }}
                  getRowClassName={(params) => {
                    const classes = [];

                    if (params.row?.assigned_package?.need_assign === true) {
                      classes.push("light-yellow-row");
                    }

                    if (isSubuserPackageRow(params.row)) {
                      classes.push("subuser-package-row");
                    }

                    if (params?.row?.id === selectedRow?.id) {
                      classes.push("selected-row-color");
                    }

                    return classes.join(" ");
                  }}
                  rowCount={(() => {
                    // Ensure rowCount is always a number
                    if (
                      typeof rowCount === "number" &&
                      !isNaN(rowCount) &&
                      isFinite(rowCount)
                    ) {
                      return rowCount;
                    }
                    // If it's an object or invalid, try to extract a number
                    if (typeof rowCount === "object" && rowCount !== null) {
                      const extracted = parseInt(rowCount.toString()) || 0;
                      return isNaN(extracted) ? 0 : extracted;
                    }
                    return 0;
                  })()}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  onRowClick={(params, event, details) => {
                    handleRowClick(params, event, details);
                  }}
                  pageSizeOptions={[rowsPerPage]}
                  rowHeight={isMobilePermits ? 88 : tabletSize ? 104 : 34}
                  disableColumnFilter={tabletSize}
                />
              )}
            </Box>
            <Box
              sx={{
                flex: isMobilePermits ? "1 1 48%" : 1,
                minHeight: 0,
                overflow: isMobilePermits ? "hidden" : "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!selectedRow ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{ textAlign: "center", px: 2 }}
                    variant={mobileSize ? "h6" : "h5"}
                    color="primary"
                  >
                    {permitPackages?.length > 0
                      ? packageRowDetailsEmptyTitle?.[activeTab]
                      : ""}
                  </Typography>
                </Box>
              ) : (
                <Grid
                  container
                  sx={{ width: "100%", height: "100%", alignItems: "stretch" }}
                >
                  <Grid
                    size={{ xs: 12, sm: 8 }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      maxHeight: "100%",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden",
                    }}
                  >
                    {selectedRow?.cst_packageID ? (
                      <Typography
                        variant="h5"
                        sx={{
                          fontStyle: "italic",
                        }}
                        align="center"
                        mt={0.2}
                        py={0.5}
                      >
                        Custom Documents
                        {selectedRow?.package_type === "noc" && (
                          <span
                            style={{
                              fontSize: "16px",
                              fontWeight: "normal",
                              marginLeft: "6px",
                            }}
                          >
                            {selectedRow?.document?.actions?.notarize &&
                            selectedRow?.document?.actions?.record
                              ? "- Notarize/Record NOC"
                              : selectedRow?.document?.actions?.notarize
                                ? "- Notarize NOC"
                                : "- Record NOC"}
                          </span>
                        )}
                      </Typography>
                    ) : activeTab !== 1 ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <FormControl
                          variant="standard"
                          sx={{
                            mt: 0.2,
                            ml: isMobilePermits ? 1 : 5,
                            width: "fit-content",
                          }}
                        >
                          <Select
                            value={
                              permitOptions.includes(selectedPermitType)
                                ? selectedPermitType
                                : ""
                            }
                            onChange={(e) =>
                              handlePermitTypeChange(e.target.value)
                            }
                            displayEmpty
                            disableUnderline
                            IconComponent={() => null}
                            renderValue={(value) => (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  fontStyle: "italic",
                                  typography: isMobilePermits ? "h6" : "h5",
                                  lineHeight: 1.1,
                                  px: isMobilePermits ? 0.5 : 0,
                                }}
                              >
                                {value && permitOptions.includes(value)
                                  ? toTitleCase(String(value))
                                  : "Select Permit Type"}
                                <ArrowDropDownIcon
                                  sx={{
                                    ml: 0.5,
                                    fontSize: isMobilePermits ? 22 : 28,
                                  }}
                                />
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  width: "fit-content",
                                  minWidth: "unset",
                                  textAlign: "center",
                                },
                              },
                            }}
                          >
                            {(permitOptions || []).map((permit) => (
                              <MenuItem
                                key={permit}
                                value={permit}
                                sx={{ textTransform: "none" }}
                              >
                                {toTitleCase(permit)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Only show Add Permit button if notarization hasn't started, isn't in progress, or completed */}
                        {(() => {
                          // Check if notarization has started, is in progress, or completed
                          const notarizationStarted =
                            selectedRow?.processing_permit?.notarization
                              ?.started;
                          const notarizationFinalized =
                            selectedRow?.processing_permit?.notarization
                              ?.finalized;
                          const isNotarized =
                            selectedRow?.notarized ||
                            selectedRow?.noc_notarized ||
                            selectedRow?.pp_notarized;
                          const notarizationInProgress =
                            notarizationStarted && !notarizationFinalized;

                          // Hide button if notarization has started, is in progress, or completed
                          const shouldHideButton =
                            notarizationStarted ||
                            notarizationInProgress ||
                            notarizationFinalized ||
                            isNotarized;

                          if (shouldHideButton) {
                            return null;
                          }

                          return (
                            <Tooltip title="Attach an additional permit to this package">
                              <span>
                                <Button
                                  disabled
                                  // disabled={
                                  //   !selectedRow?.documents?.noc?.is_created
                                  // }
                                  size="small"
                                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                                  variant="outlined"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    mt: 0.5,
                                    mr: 0.5,
                                    ml: 2,
                                    flexShrink: 0,
                                    fontWeight: 500,
                                    textTransform: "none",
                                    borderRadius: 1.5,
                                    px: 2.5,
                                    py: 1,
                                    borderColor: "rgba(0,0,0,0.12)",
                                    color: "text.primary",
                                    backgroundColor: "white",
                                    "&:hover": {
                                      borderColor: "primary.main",
                                      backgroundColor:
                                        "rgba(25, 118, 210, 0.04)",
                                      color: "primary.main",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                  onClick={handleAttachingNewPermit}
                                >
                                  Add Permit
                                </Button>
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </Box>
                    ) : (
                      <Typography
                        variant="h5"
                        sx={{
                          fontStyle: "italic",
                        }}
                        align="center"
                        mt={0.2}
                        py={0.5}
                      >
                        Packages Sent to Contractors
                      </Typography>
                    )}

                    {!selectedRow?.cst_packageID && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 3,
                          py: 0,
                          borderBottom: "1px solid rgba(0,0,0,0.08)",
                          backgroundColor: "rgba(0,0,0,0.02)",
                        }}
                      >
                        {selectedRowIsSubuserPackage && (
                          <Chip
                            size="small"
                            label="Subuser package"
                            color="info"
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                        )}
                        {selectedRow?.notarized || selectedRow?.pp_notarized ? (
                          <LoadingButton
                            variant="text"
                            endIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                            size="medium"
                            loading={ppDownloading}
                            onClick={() =>
                              dispatch(handleDownloadNoc("all", "legal"))
                            }
                            sx={{
                              fontWeight: 500,
                              textTransform: "none",
                              color: "primary.main",
                              px: 2,
                              py: 1,
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                              },
                            }}
                          >
                            Download Package
                          </LoadingButton>
                        ) : (
                          <LoadingButton
                            disabled
                            variant="text"
                            endIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                            size="medium"
                            loading={ppDownloading}
                            onClick={() => {}}
                            sx={{
                              fontWeight: 500,
                              textTransform: "none",
                              px: 2,
                              py: 1,
                            }}
                          >
                            Download Package
                          </LoadingButton>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.875rem",
                            color: "text.secondary",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: "text.secondary",
                              "& .number": {
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              },
                            }}
                          >
                            Forms{" "}
                            <Box component="span" className="number">
                              {getSelectedDocumentsForPermitType(
                                selectedPermitType,
                              )?.filter((doc) => doc.created === true).length ||
                                0}
                            </Box>
                            <Box
                              component="span"
                              sx={{ mx: 0.5, opacity: 0.6 }}
                            >
                              /
                            </Box>
                            <Box component="span" className="number">
                              {getSelectedDocumentsForPermitType(
                                selectedPermitType,
                              )?.length || 0}
                            </Box>
                          </Typography>

                          <Box
                            sx={{
                              width: "1px",
                              height: "16px",
                              backgroundColor: "divider",
                              opacity: 0.4,
                            }}
                          />

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: "text.secondary",
                              "& .number": {
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              },
                            }}
                          >
                            Permits{" "}
                            <Box component="span" className="number">
                              {permitOptions?.length || 0}
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* LEFT CONTENT STARTS */}
                    <Box
                      sx={{
                        display: "flex",
                        height: isMobilePermits ? "100%" : "calc(100% - 90px)",
                        flex: isMobilePermits ? 1 : undefined,
                        minHeight: 0,
                        mt: 0,
                        borderTop: "4px solid #d3d3d3",
                        flexDirection: isMobilePermits ? "column" : "row",
                      }}
                    >
                      {isMobilePermits ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                          }}
                        >
                          <Box
                            sx={{
                              px: 1,
                              py: 1,
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              backgroundColor: "#fff",
                              flex: "0 0 auto",
                            }}
                          >
                            <ToggleButtonGroup
                              value={mobileDetailsView}
                              exclusive
                              fullWidth
                              size="small"
                              onChange={(_, next) => {
                                if (next) {
                                  setMobileDetailsView(next);
                                }
                              }}
                              sx={{
                                backgroundColor: "#f7f7fb",
                                borderRadius: 2,
                                p: 0.3,
                                "& .MuiToggleButton-root": {
                                  border: "none",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  flex: 1,
                                  py: 0.75,
                                },
                                "& .Mui-selected": {
                                  backgroundColor: "#fff",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                },
                              }}
                            >
                              {!selectedRow?.cst_packageID && (
                                <ToggleButton value="docs">Docs</ToggleButton>
                              )}
                              <ToggleButton value="status">Status</ToggleButton>
                              <ToggleButton value="details">
                                Details
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          {/* scrollable panel */}
                          <Box
                            sx={{
                              flex: "1 1 auto",
                              minHeight: 0,
                              overflowY: "auto",
                              backgroundColor: "#fff",
                            }}
                          >
                            {!selectedRow ? null : (
                              <>
                                {mobileDetailsView === "docs" && (
                                  <Box sx={{ width: "100%" }}>
                                    {mobileDocTabs}
                                  </Box>
                                )}

                                {mobileDetailsView === "status" && (
                                  <Box sx={{ width: "100%" }}>
                                    {middleContent}
                                  </Box>
                                )}

                                {mobileDetailsView === "details" && (
                                  <Box sx={{ width: "100%" }}>
                                    {memoizedRightContent}
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {selectedRow?.cst_packageID ? (
                            <></>
                          ) : (
                            <Box
                              className="step14"
                              sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                height: "100%",
                                maxHeight: "100%",
                                flexDirection: "column",
                                alignSelf: "stretch",
                                minHeight: 0,
                                overflowY: "auto",
                                minWidth: "16rem",
                              }}
                            >
                              <Tabs
                                orientation="vertical"
                                value={leftValue}
                                onChange={handleLeftChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                aria-label="scrollable auto tabs example"
                                slotProps={{
                                  indicator: {
                                    sx: {
                                      left: 0, // Position the indicator on the left side
                                      width: "3px", // Adjust the width of the indicator
                                    },
                                  },
                                }}
                                sx={{
                                  alignItems: "flex-start", // Align the tabs to the left
                                  flex: 1,
                                  minHeight: 0,
                                  maxHeight: "100%",
                                  overflowY: "auto",
                                }}
                              >
                                {getSelectedDocumentsForPermitType(
                                  selectedPermitType,
                                )?.map((document, index) => {
                                  const selectedDocs =
                                    getSelectedDocumentsForPermitType(
                                      selectedPermitType,
                                    );
                                  const isEnabled =
                                    selectedRow?.permit_package_created ||
                                    document?.created ||
                                    (selectedDocs?.[index - 1]?.created &&
                                      !document?.created);

                                  return (
                                    <Tab
                                      disabled={!isEnabled}
                                      key={index}
                                      label={
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "flex-start",
                                            textAlign: "left",
                                          }}
                                        >
                                          {document.label}
                                          {selectedDocs?.[index]?.created &&
                                          selectedDocs?.[index]?.name ===
                                            "noc" &&
                                          (selectedRow?.notarized === true ||
                                            selectedRow?.noc_notarized ===
                                              true) ? (
                                            <CheckCircleIcon
                                              style={{
                                                color: green[500],
                                                marginLeft: 8,
                                                fontSize: "20px",
                                              }}
                                            />
                                          ) : selectedDocs?.[index]?.name !==
                                              "noc" &&
                                            (selectedRow?.notarized === true ||
                                              selectedRow?.pp_notarized ===
                                                true) ? (
                                            <CheckCircleIcon
                                              style={{
                                                color: green[500],
                                                marginLeft: 8,
                                                fontSize: "20px",
                                              }}
                                            />
                                          ) : selectedRow?.processing_permit?.notarization?.final_documents?.find(
                                              (doc) =>
                                                doc?.name ===
                                                selectedDocs?.[index]?.name,
                                            ) ? (
                                            <CheckCircleIcon
                                              style={{
                                                color: green[500],
                                                marginLeft: 8,
                                                fontSize: "20px",
                                              }}
                                            />
                                          ) : selectedDocs?.[index]?.created ? (
                                            <CheckCircleIcon
                                              style={{
                                                color: grey[500],
                                                marginLeft: 8,
                                                fontSize: "20px",
                                              }}
                                            />
                                          ) : (
                                            <></>
                                          )}
                                        </span>
                                      }
                                      disableRipple
                                      sx={{
                                        py: 0,
                                        alignItems: "flex-start", // Align the tabs to the left
                                        marginLeft: "24px",
                                        ".MuiTab-root": {
                                          justifyContent: "flex-start", // Align the tab content to the left
                                          textAlign: "left", // Align the text to the left
                                          // Set a consistent left margin for all tabs
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Tabs>
                            </Box>
                          )}

                          {/* MIDDLE CONTENT */}
                          {middleContent}
                          {/* MIDDLE CONTENT ENDS */}
                        </>
                      )}
                    </Box>
                  </Grid>

                  {/* RIGHT CONTENT */}
                  {!isMobilePermits && memoizedRightContent}
                </Grid>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      <SelectDownloadFileSIzeModal
        open={fileSizeModalOpen}
        setOpen={setFileSizeModalOpen}
      />

      <Snackbar
        sx={{ zIndex: 9999999999999 }}
        open={myPermitsSnackOpen}
        autoHideDuration={5000}
        onClose={snackHandleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={snackHandleClose}
          severity="success" // Changed severity to success
          sx={{
            width: "100%",
            marginTop: "80px",
            marginLeft: lg ? "280px" : "0px",
            backgroundColor: "#388e3c", // Green shade for success
            color: "#fff",
            fontWeight: "bold",
          }}
          iconMapping={{
            success: <CheckCircleIcon sx={{ color: "#fff" }} />, // Changed to success icon
          }}
        >
          {myPermitsSnackText}
        </Alert>
      </Snackbar>

      {/* Search & Filter Modal */}
      <Dialog
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        fullScreen={mobileSize} // optional
        maxWidth={false}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              maxHeight: mobileSize ? "100vh" : "90vh",
              width: "100%",
              margin: 0, // removes default margins
              animation: searchModalOpen
                ? "modalSlideIn 0.3s ease-out"
                : "modalSlideOut 0.3s ease-in",
            },
          },
        }}
        sx={{
          "& .MuiDialog-paper": {
            "@keyframes modalSlideIn": {
              from: {
                opacity: 0,
                transform: "translateY(-50px) scale(0.95)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
            "@keyframes modalSlideOut": {
              from: {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
              to: {
                opacity: 0,
                transform: "translateY(-50px) scale(0.95)",
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" component="div">
            Search & Filter Permit Packages
          </Typography>
          <IconButton
            onClick={() => setSearchModalOpen(false)}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {isBranchManager && (
              <>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
                  >
                    Packages
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={searchFilters.includeSubuserPackages}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setSearchFilters((prev) => ({
                              ...prev,
                              includeSubuserPackages: checked,
                              subuserEmail: checked ? prev.subuserEmail : "",
                            }));
                          }}
                        />
                      }
                      label="Include subuser packages"
                      sx={{ m: 0 }}
                    />

                    {searchFilters.includeSubuserPackages && (
                      <Autocomplete
                        options={branchSubuserOptions}
                        value={
                          branchSubuserOptions.find(
                            (option) =>
                              option.email === searchFilters.subuserEmail,
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            subuserEmail: newValue?.email || "",
                          }))
                        }
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : option?.label || option?.email || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.email === value.email
                        }
                        slotProps={{
                          paper: {
                            sx: {
                              maxHeight: 280,
                              overflowY: "auto",
                            },
                          },
                          listbox: {
                            sx: {
                              maxHeight: 280,
                              overflowY: "auto",
                            },
                          },
                        }}
                        sx={{ minWidth: 280, maxWidth: 420, flex: 1 }}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Subuser"
                            placeholder="All subusers"
                          />
                        )}
                      />
                    )}
                  </Box>
                </Box>

                <Divider />
              </>
            )}

            {/* Text Search Fields */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Property Details
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 2,
                }}
              >
                <Autocomplete
                  options={autocompleteOptions.packageNames}
                  value={searchFilters.packageName}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      packageName: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      packageName: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Package Name" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.projectAddresses}
                  value={searchFilters.projectAddress}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      projectAddress: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      projectAddress: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Project Address" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.propertyAddresses}
                  value={searchFilters.propertyAddress}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      propertyAddress: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      propertyAddress: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Property Address" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.propertyOwnerNames}
                  value={searchFilters.propertyOwnerName}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      propertyOwnerName: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      propertyOwnerName: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Property Owner Name" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.counties}
                  value={searchFilters.county}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      county: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      county: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="County" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.cities}
                  value={searchFilters.city}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      city: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      city: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="City" />
                  )}
                />
              </Box>
            </Box>

            <Divider />

            {/* Contractor Information */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Contractor Information
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 2,
                }}
              >
                <Autocomplete
                  options={autocompleteOptions.contractorNames}
                  value={searchFilters.contractorName}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      contractorName: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      contractorName: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Contractor Business Name" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.contractorLicenseNumbers}
                  value={searchFilters.contractorLicenseNumber}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      contractorLicenseNumber: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      contractorLicenseNumber: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Contractor License Number" />
                  )}
                />
                <Autocomplete
                  options={autocompleteOptions.contractorLicenseTypes}
                  value={searchFilters.contractorLicenseType}
                  onChange={(event, newValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      contractorLicenseType: newValue || "",
                    }))
                  }
                  onInputChange={(event, newInputValue) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      contractorLicenseType: newInputValue,
                    }))
                  }
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Contractor License Type" />
                  )}
                />
              </Box>
            </Box>

            <Divider />

            {/* Status Filters */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Status Filters
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 2,
                }}
              >
                {/* NOC Created */}
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    NOC Created
                  </FormLabel>
                  <RadioGroup
                    value={searchFilters.nocCreated}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        nocCreated: e.target.value,
                      }))
                    }
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label="No"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="Any"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Permit Package Created */}
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    Permit Package Created
                  </FormLabel>
                  <RadioGroup
                    value={searchFilters.permitPackageCreated}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        permitPackageCreated: e.target.value,
                      }))
                    }
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label="No"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="Any"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Notarized */}
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    Notarized
                  </FormLabel>
                  <RadioGroup
                    value={searchFilters.notarized}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        notarized: e.target.value,
                      }))
                    }
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label="No"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="Any"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Recorded */}
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    Recorded
                  </FormLabel>
                  <RadioGroup
                    value={searchFilters.recorded}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        recorded: e.target.value,
                      }))
                    }
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label="No"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="Any"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Initiated Date Range */}
                <FormControl component="fieldset" sx={{ gridColumn: "span 2" }}>
                  <FormLabel
                    component="legend"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    Initiated Date Range
                  </FormLabel>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="en"
                    >
                      <DatePicker
                        label="From"
                        format="MM/DD/YYYY"
                        value={
                          searchFilters.initiatedFrom
                            ? dayjs(searchFilters.initiatedFrom).utc()
                            : null
                        }
                        onChange={(newValue) => {
                          setSearchFilters((prev) => ({
                            ...prev,
                            initiatedFrom: newValue
                              ? newValue
                                  .startOf("day")
                                  .utc()
                                  .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
                              : "",
                          }));
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { minWidth: 140, maxWidth: 160 },
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mx: 1 }}
                      >
                        to
                      </Typography>
                      <DatePicker
                        label="Until"
                        format="MM/DD/YYYY"
                        value={
                          searchFilters.initiatedUntil
                            ? dayjs(searchFilters.initiatedUntil).utc()
                            : null
                        }
                        onChange={(newValue) => {
                          setSearchFilters((prev) => ({
                            ...prev,
                            initiatedUntil: newValue
                              ? newValue
                                  .endOf("day")
                                  .utc()
                                  .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
                              : "",
                          }));
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { minWidth: 140, maxWidth: 160 },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </FormControl>

                {/* Connected to City */}
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    Connected to City
                  </FormLabel>
                  <RadioGroup
                    value={searchFilters.connectedToCity}
                    onChange={(e) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        connectedToCity: e.target.value,
                      }))
                    }
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label="No"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="Any"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            {/* Active Filters Display */}
            {currentFilters && Object.keys(currentFilters).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Active Filters:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(currentFilters).map(([key, value]) => {
                    // Format the label based on field type
                    const formatLabel = (key, value) => {
                      if (key === "includeSubuserPackages") {
                        return "Include Subuser Packages";
                      }

                      if (key === "subuserEmail") {
                        const selectedSubuser = branchSubuserOptions.find(
                          (subuser) => subuser.email === value,
                        );
                        return `Subuser: ${selectedSubuser?.label || value}`;
                      }

                      const fieldName = key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase());

                      if (key === "initiatedFrom" || key === "initiatedUntil") {
                        const date = new Date(value);
                        return `${fieldName}: ${date.toLocaleDateString()}`;
                      }

                      return `${fieldName}: ${value}`;
                    };

                    return (
                      <Chip
                        key={key}
                        label={formatLabel(key, value)}
                        onDelete={async () => {
                          setIsApplyingFilters(true);

                          try {
                            const newFilters = { ...currentFilters };
                            delete newFilters[key];

                            if (key === "includeSubuserPackages") {
                              delete newFilters.subuserEmail;
                            }

                            setCurrentFilters(
                              Object.keys(newFilters).length > 0
                                ? newFilters
                                : null,
                            );
                            dispatch(setSelectedRow(null));

                            // Clear the corresponding search filter field
                            setSearchFilters((prev) => {
                              if (key === "includeSubuserPackages") {
                                return {
                                  ...prev,
                                  includeSubuserPackages: false,
                                  subuserEmail: "",
                                };
                              }

                              return {
                                ...prev,
                                [key]: "",
                              };
                            });

                            dispatch(setPage(0));
                            await fetchData(
                              0,
                              rowsPerPage,
                              Object.keys(newFilters).length > 0
                                ? newFilters
                                : null,
                              true,
                            );
                          } catch (error) {
                            console.error("Error removing filter:", error);
                          } finally {
                            setIsApplyingFilters(false);
                          }
                        }}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={async () => {
              setIsApplyingFilters(true);

              try {
                setSearchFilters({
                  includeSubuserPackages: false,
                  subuserEmail: "",
                  packageName: "",
                  projectAddress: "",
                  propertyAddress: "",
                  propertyOwnerName: "",
                  contractorLicenseNumber: "",
                  contractorLicenseType: "",
                  contractorName: "",
                  county: "",
                  city: "",
                  nocCreated: "",
                  permitPackageCreated: "",
                  notarized: "",
                  recorded: "",
                  connectedToCity: "",
                  initiatedFrom: "",
                  initiatedUntil: "",
                });
                // Clear current filters and reset to page 0
                setCurrentFilters(null);
                dispatch(setSelectedRow(null));
                dispatch(setPage(0));
                await fetchData(0, rowsPerPage, null, true);
              } catch (error) {
                console.error("Error clearing filters:", error);
              } finally {
                setIsApplyingFilters(false);
              }
            }}
            color="secondary"
            variant="outlined"
            disabled={isApplyingFilters}
            startIcon={
              isApplyingFilters ? <CircularProgress size={16} /> : null
            }
          >
            {isApplyingFilters ? "Clearing..." : "Clear All"}
          </Button>
          <Button
            onClick={() => setSearchModalOpen(false)}
            variant="outlined"
            disabled={isApplyingFilters}
          >
            Close
          </Button>
          <Button
            onClick={async () => {
              // Apply filters and reset to page 0
              const filtersToApply = Object.keys(searchFilters).reduce(
                (acc, key) => {
                  if (key === "includeSubuserPackages") {
                    if (searchFilters[key] === true) {
                      acc[key] = true;
                    }
                  } else if (searchFilters[key] !== "") {
                    acc[key] = searchFilters[key];
                  }
                  return acc;
                },
                {},
              );

              setIsApplyingFilters(true);

              try {
                setCurrentFilters(
                  Object.keys(filtersToApply).length > 0
                    ? filtersToApply
                    : null,
                );
                dispatch(setSelectedRow(null));
                dispatch(setPage(0));
                await fetchData(
                  0,
                  rowsPerPage,
                  Object.keys(filtersToApply).length > 0
                    ? filtersToApply
                    : null,
                  true,
                );
              } catch (error) {
                console.error("Error applying filters:", error);
              } finally {
                setIsApplyingFilters(false);
              }
            }}
            variant="contained"
            disabled={isApplyingFilters}
            startIcon={
              isApplyingFilters ? <CircularProgress size={16} /> : null
            }
          >
            {isApplyingFilters ? "Applying Filters..." : "Apply Filters"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(MyPermitGrid);
